import { useSession, useUser } from '@clerk/clerk-react';
import { useRef, useState } from 'react';
import { useAuth } from '@/auth/useAuth';
import { useChangePassword } from '@/features/users';
import { normalizeClerkErrorMessage } from '@/pages/auth-error-utils';
import { getMessage } from '@/shared/copy/messages';
import { useCopyNotifications } from '@/shared/copy/notifications';
import { useMutationError } from '@/shared/hooks/useMutationError';
import { isStrongPassword, strongPasswordMessage } from '@/shared/validation/passwordPolicy';
import type { AuthSession } from '@/auth/types';

export type PasswordChangeFormState = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  panelAlertMessage: string | null;
  showCurrentPassword: boolean;
  showNewPassword: boolean;
  showConfirmPassword: boolean;
  reverificationDialogOpen: boolean;
  reverificationPassword: string;
  reverificationError: string | null;
  reverificationSubmitting: boolean;
  saveDisabled: boolean;
  saving: boolean;
  onCurrentPasswordChange: (value: string) => void;
  onNewPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  onToggleCurrentPassword: () => void;
  onToggleNewPassword: () => void;
  onToggleConfirmPassword: () => void;
  onReverificationPasswordChange: (value: string) => void;
  onReverificationCancel: () => void;
  onReverificationSubmit: () => void;
  onSave: () => void;
};

type UsePasswordChangeFormArgs = {
  session: AuthSession | null;
  refreshUser?: () => Promise<unknown>;
  externalAuthProvider?: string | null;
};

export const usePasswordChangeForm = ({
  session,
  refreshUser,
  externalAuthProvider,
}: UsePasswordChangeFormArgs): PasswordChangeFormState => {
  const { user: clerkUser } = useUser();
  const { session: clerkSession } = useSession();
  const { refreshSession } = useAuth();
  const passwordMutation = useChangePassword();
  const passwordError = useMutationError();
  const { notifyInfo, notifySuccess } = useCopyNotifications();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [reverificationDialogOpen, setReverificationDialogOpen] = useState(false);
  const [reverificationPassword, setReverificationPassword] = useState('');
  const [reverificationError, setReverificationError] = useState<string | null>(null);
  const [reverificationSubmitting, setReverificationSubmitting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [panelAlertMessage, setPanelAlertMessage] = useState<string | null>(null);
  const pendingReverificationAction = useRef<(() => Promise<void>) | null>(null);

  const saveDisabled = isSubmitting || passwordMutation.isPending || !session;
  const hasClerkErrorCode = (error: unknown, code: string): boolean => {
    if (typeof error !== 'object' || error === null || !('errors' in error)) {
      return false;
    }
    const errors = (error as { errors?: Array<{ code?: string }> }).errors;
    return Array.isArray(errors) && errors.some((entry) => entry.code === code);
  };

  const closeReverificationDialog = (): void => {
    pendingReverificationAction.current = null;
    setReverificationDialogOpen(false);
    setReverificationPassword('');
    setReverificationError(null);
    setReverificationSubmitting(false);
  };

  const openReverificationDialog = (action: () => Promise<void>): void => {
    pendingReverificationAction.current = action;
    setReverificationPassword('');
    setReverificationError(null);
    setReverificationDialogOpen(true);
  };

  const submitClerkPasswordUpdate = async (
    input: { currentPassword: string; newPassword: string },
    onSaveSuccess: () => void,
  ): Promise<void> => {
    if (!clerkUser) {
      setPanelAlertMessage(getMessage('account.password.unableToChange'));
      setIsSubmitting(false);
      return;
    }
    try {
      await clerkUser.updatePassword(input);
      onSaveSuccess();
    } catch (err: unknown) {
      if (hasClerkErrorCode(err, 'session_reverification_required')) {
        setIsSubmitting(false);
        openReverificationDialog(async () => {
          setIsSubmitting(true);
          await submitClerkPasswordUpdate(input, onSaveSuccess);
        });
        return;
      }
      if (hasClerkErrorCode(err, 'reverification_cancelled')) {
        setIsSubmitting(false);
        setPanelAlertMessage(getMessage('account.password.changeCancelled'));
        return;
      }
      const message = passwordErrorMessage(err);
      passwordError.setError(new Error(message));
      setPanelAlertMessage(message);
      setIsSubmitting(false);
    }
  };

  const handleChangePassword = (): void => {
    if (!session) return;
    passwordError.clearError();
    setPanelAlertMessage(null);
    if (!isStrongPassword(newPassword)) {
      setPanelAlertMessage(strongPasswordMessage);
      return;
    }
    if (newPassword !== confirmPassword) {
      setPanelAlertMessage(getMessage('account.password.passwordMismatch'));
      return;
    }
    if (currentPassword.trim().length === 0) {
      setPanelAlertMessage(getMessage('account.password.currentRequired'));
      return;
    }
    if (currentPassword === newPassword) {
      setPanelAlertMessage(getMessage('account.password.mustDiffer'));
      return;
    }
    const onSaveSuccess = (): void => {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPanelAlertMessage(null);
      notifySuccess('account.password.updated');
      passwordError.clearError();
      setIsSubmitting(false);
      void Promise.all([refreshSession(), refreshUser?.()]);
    };
    setIsSubmitting(true);
    if (externalAuthProvider === 'clerk') {
      if (!clerkUser) {
        setPanelAlertMessage(getMessage('account.password.unableToChange'));
        setIsSubmitting(false);
        return;
      }
      void submitClerkPasswordUpdate({ currentPassword, newPassword }, onSaveSuccess);
      return;
    }
    passwordMutation.mutate(
      { id: session.userId, currentPassword, newPassword },
      {
        onSuccess: () => {
          onSaveSuccess();
        },
        onError: (err) => {
          const message = passwordErrorMessage(err);
          passwordError.setError(new Error(message));
          setPanelAlertMessage(message);
          setIsSubmitting(false);
        },
      },
    );
  };

  const passwordErrorMessage = (error: unknown): string =>
    normalizeClerkErrorMessage(error, getMessage('account.password.unableToChange'));

  const handleReverificationSubmit = (): void => {
    const password = reverificationPassword.trim();
    if (!password) {
      setReverificationError(getMessage('auth.common.passwordRequired'));
      return;
    }
    if (!clerkSession) {
      setReverificationError(getMessage('auth.common.authSessionLoading'));
      return;
    }

    setReverificationSubmitting(true);
    setReverificationError(null);
    void clerkSession
      .startVerification({ level: 'first_factor' })
      .then(async () => {
        const verification = await clerkSession.attemptFirstFactorVerification({
          strategy: 'password',
          password,
        });
        if (verification.status !== 'complete') {
          throw new Error(getMessage('auth.common.reverificationAdditionalRequired'));
        }
        const resumeAction = pendingReverificationAction.current;
        closeReverificationDialog();
        if (resumeAction) {
          await resumeAction();
        }
      })
      .catch((error: unknown) => {
        if (hasClerkErrorCode(error, 'reverification_cancelled')) {
          closeReverificationDialog();
          setIsSubmitting(false);
          notifyInfo('account.password.changeCancelled');
          return;
        }
        setReverificationError(
          normalizeClerkErrorMessage(error, getMessage('auth.common.reverificationFailed')),
        );
      })
      .finally(() => {
        setReverificationSubmitting(false);
      });
  };

  return {
    currentPassword,
    newPassword,
    confirmPassword,
    panelAlertMessage,
    showCurrentPassword,
    showNewPassword,
    showConfirmPassword,
    reverificationDialogOpen,
    reverificationPassword,
    reverificationError,
    reverificationSubmitting,
    saveDisabled,
    saving: isSubmitting || passwordMutation.isPending,
    onCurrentPasswordChange: (value) => {
      setCurrentPassword(value);
      setPanelAlertMessage(null);
    },
    onNewPasswordChange: (value) => {
      setNewPassword(value);
      setPanelAlertMessage(null);
    },
    onConfirmPasswordChange: (value) => {
      setConfirmPassword(value);
      setPanelAlertMessage(null);
    },
    onToggleCurrentPassword: () => {
      setShowCurrentPassword((prev) => !prev);
    },
    onToggleNewPassword: () => {
      setShowNewPassword((prev) => !prev);
    },
    onToggleConfirmPassword: () => {
      setShowConfirmPassword((prev) => !prev);
    },
    onReverificationPasswordChange: setReverificationPassword,
    onReverificationCancel: closeReverificationDialog,
    onReverificationSubmit: handleReverificationSubmit,
    onSave: handleChangePassword,
  };
};
